/**
 * "Proc overlay" setting (the screen-side spell activation flourish, cluster/procOverlay.ts).
 * On by default. Persisted in localStorage; every storage access is in try/catch.
 */
import { el } from '../dom';

export const PROC_OVERLAY_KEY = 'keystone.procOverlay.v1';

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadProcOverlay(s: Pick<Storage, 'getItem'> | null = storage()): boolean {
  try {
    return s?.getItem(PROC_OVERLAY_KEY) !== 'off';
  } catch {
    return true;
  }
}

type Listener = (on: boolean) => void;

class ProcOverlayStore {
  private value: boolean | null = null;
  private readonly listeners = new Set<Listener>();

  get(): boolean {
    return (this.value ??= loadProcOverlay());
  }

  set(on: boolean): void {
    this.value = on;
    try {
      storage()?.setItem(PROC_OVERLAY_KEY, on ? 'on' : 'off');
    } catch {
      /* storage blocked: the setting lasts for this session */
    }
    for (const l of this.listeners) l(on);
  }

  /** Calls `fn` now and on every change. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.get());
    return () => this.listeners.delete(fn);
  }
}

export const procOverlaySettings = new ProcOverlayStore();

/** Adds the "Proc overlay" checkbox to a settings panel. Returns a sync function. */
export function mountProcOverlayControl(panel: HTMLElement, before: Node | null = null): () => void {
  const r = el('label', 'gfx-row check');
  panel.insertBefore(r, before);
  el('span', '', r, 'Proc overlay (screen sides)');
  const input = el('input', '', r);
  input.type = 'checkbox';
  input.addEventListener('change', () => procOverlaySettings.set(input.checked));
  return () => {
    input.checked = procOverlaySettings.get();
  };
}
